// CTSK-525 — hover-chip actions (Watch / Pipeline / More menu) for pages that
// render DocumentCardWall outside the Documents list.
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DocumentListItem } from '@captain-tasko/shared';
import { apiSingle } from '../../lib/api.js';
import { copyToClipboard } from '../../lib/clipboard.js';
import { toast } from '../Toast.js';
import type { CardActions } from './DocumentCard.js';
import { rememberOpened } from './viewPrefs.js';

export function useDocCardActions(onNavigate: (p: string) => void): CardActions {
  const qc = useQueryClient();
  return useMemo<CardActions>(() => ({
    onWatch: (d: DocumentListItem) => {
      apiSingle(`/documents/${d.id}/watch`, { method: d.isWatchedByMe ? 'DELETE' : 'POST' })
        .then(() => qc.invalidateQueries({ queryKey: ['documents'] }))
        .catch((e: Error) => toast.error(e.message));
    },
    onPipeline: (d) => { rememberOpened(d.id); onNavigate(`/documents/${d.id}#pipeline-output`); },
    onCopyLink: (d) => { void copyToClipboard(`${window.location.origin}/documents/${d.id}`).then(() => toast.success('Link copied')); },
    onFindMore: (d) => {
      const first = d.tags[0];
      onNavigate(first ? `/documents?tag=${encodeURIComponent(first.id)}` : '/documents');
    },
    onTimelane: (d) => { rememberOpened(d.id); onNavigate('/tools/timeline-lane'); },
    onPostNote: (d) => {
      const text = window.prompt(`Note on "${d.title}":`);
      if (!text || !text.trim()) return;
      apiSingle('/notes', { method: 'POST', body: { body: text.trim(), targetType: 'document', targetId: d.id } })
        .then(() => toast.success('Note posted'))
        .catch((e: Error) => toast.error(e.message));
    },
    onPostComment: (d) => { rememberOpened(d.id); onNavigate(`/documents/${d.id}?tab=activity`); },
  }), [qc, onNavigate]);
}
